/**
 * Multibuy Manager Custom Element
 * Handles product selection and mini basket functionality
 */
class MultibuyManager extends HTMLElement {
  constructor() {
    super();
    this.selectedMainProduct = null;
    this.selectedSecondaryProducts = new Map(); // Map of productId -> {product, quantity}
    this.sectionId = this.dataset.sectionId;
    this.storageKey = 'multibuy_selections';
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.cacheElements();
    this.cacheLocaleStrings();
    this.disableOutOfStockVariants();
    this.attachEventListeners();
    this.initializeSecondaryVisibility();
    this.initProgressHeader();
    this.initMobileBasketFooterDetection();
    this.restoreSelections();
    this.updateMiniBasket();
    this.updateSecondaryCardsState();
  }

  cacheLocaleStrings() {
    // Cache locale strings from footer data attributes
    const footer = this.querySelector('.multibuy-mini-basket__footer');
    if (footer) {
      this.locale = {
        subtotal: footer.dataset.subtotalLabel,
        freeCharmOne: footer.dataset.freeCharmOne,
        freeCharmOther: footer.dataset.freeCharmOther,
        discount: footer.dataset.discountLabel,
        total: footer.dataset.totalLabel,
        addButton: footer.dataset.addButton,
        addButtonItems: footer.dataset.addButtonItems,
        shippingInfo: footer.dataset.shippingInfo,
        freeLabel: footer.dataset.freeLabel,
        quantityLabel: footer.dataset.quantityLabel || 'Qty'
      };
    }

    // Cache progress bar locale strings
    const progressBar = this.querySelector('.multibuy-progress-bar');
    if (progressBar) {
      this.progressLocale = {
        messageOne: progressBar.dataset.messageOne,
        messageOther: progressBar.dataset.messageOther,
        messageOneWithStoppers: progressBar.dataset.messageOneWithStoppers,
        messageOtherWithStoppers: progressBar.dataset.messageOtherWithStoppers,
        badge: progressBar.dataset.badge
      };
    }
  }

  disableOutOfStockVariants() {
    // Disable swatches for out-of-stock variants
    this.productCards.forEach(card => {
      // Find all disabled swatches
      const disabledSwatches = card.querySelectorAll('.is-disabled');
      
      disabledSwatches.forEach(swatch => {
        // Disable the input
        const input = swatch.querySelector('input[type="radio"]');
        if (input) {
          input.disabled = true;
        }
        
        // Prevent click events
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }, true);
        
        // If it's a link, prevent navigation
        if (swatch.tagName === 'A') {
          swatch.addEventListener('click', (e) => {
            e.preventDefault();
            return false;
          });
        }
      });
    });
  }

  initializeSecondaryVisibility() {
    // Get the currently active tab
    const activeTab = this.querySelector('.multibuy-tab--active');
    if (!activeTab) return;
    
    const activeCollection = activeTab.getAttribute('data-collection');
    
    // Show/hide secondary sections based on active tab
    this.secondarySections.forEach(section => {
      const secondaryFor = section.getAttribute('data-secondary-for');
      if (secondaryFor && secondaryFor.includes(activeCollection)) {
        section.style.display = 'block';
      } else {
        section.style.display = 'none';
      }
    });
  }

  cacheElements() {
    // Tabs
    this.tabs = this.querySelectorAll('.multibuy-tab');
    this.mainCollections = this.querySelectorAll('.multibuy-collection');
    this.secondarySections = this.querySelectorAll('.multibuy-section--secondary');
    
    // Product cards
    this.productCards = this.querySelectorAll('.product-card--selectable');
    
    // Mini basket elements
    this.miniBasketMobile = this.querySelector('.multibuy-mini-basket-mobile');
    this.miniBasketToggle = this.querySelector('.multibuy-mini-basket__mobile-toggle');
    this.resetButton = this.querySelector('.multibuy-mini-basket__reset');
    
    // Mini basket content elements
    this.emptyStates = this.querySelectorAll('.multibuy-mini-basket__empty');
    this.productStates = this.querySelectorAll('.multibuy-mini-basket__product');
    this.totalPrices = this.querySelectorAll('.multibuy-mini-basket__total-price');
    this.addButtons = this.querySelectorAll('.multibuy-mini-basket__add-button');
    this.images = this.querySelectorAll('.multibuy-mini-basket__product-image img');
    this.titles = this.querySelectorAll('.multibuy-mini-basket__product-title');
    this.variants = this.querySelectorAll('.multibuy-mini-basket__product-variant');
    this.prices = this.querySelectorAll('.multibuy-mini-basket__product-price');
  }

  attachEventListeners() {
    // Tab switching
    this.tabs.forEach(tab => {
      tab.addEventListener('click', (e) => this.handleTabClick(e));
    });

    // Tag filters
    this.initTagFilters();

    // Load more buttons
    this.initLoadMore();

    // Intercept quick buy modals for multibuy products
    this.initQuickBuyIntercept();

    // Product card selection
    this.productCards.forEach(card => {
      this.attachProductCardListeners(card);
    });

    // Mobile basket toggle
    if (this.miniBasketToggle) {
      this.miniBasketToggle.addEventListener('click', () => this.toggleMobileBasket());
    }

    // Reset button
    if (this.resetButton) {
      this.resetButton.addEventListener('click', () => this.resetSelection());
    }

    // Add to cart buttons
    this.addButtons.forEach(btn => {
      btn.addEventListener('click', () => this.addToCart());
    });

    // Remove product buttons (delegated event listener for dynamically added buttons)
    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.multibuy-mini-basket__remove-product');
      if (removeBtn) {
        this.handleRemoveProduct(removeBtn);
      }
    });
  }

  attachProductCardListeners(card) {
    // Skip if already initialized
    if (card.dataset.listenersAttached) {
      return;
    }
    card.dataset.listenersAttached = 'true';
    
    // Handle swatch changes
    const swatchInputs = card.querySelectorAll('.product-card__info [type="radio"]');
    swatchInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleSwatchChange(e, card));
    });

    // Handle card click for selection
    card.addEventListener('click', (e) => this.handleCardClick(e, card));

    // Handle quantity selector for multiple selection cards
    const selectionType = card.getAttribute('data-selection-type');
    if (selectionType === 'multiple') {
      const decreaseBtn = card.querySelector('.product-card__quantity-decrease');
      const increaseBtn = card.querySelector('.product-card__quantity-increase');
      
      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.handleQuantityChange(card, -1);
        });
      }
      
      if (increaseBtn) {
        increaseBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.handleQuantityChange(card, 1);
        });
      }
    }
  }

  handleTabClick(e) {
    const targetCollection = e.currentTarget.getAttribute('data-collection');

    this.tabs.forEach(t => t.classList.remove('multibuy-tab--active'));
    e.currentTarget.classList.add('multibuy-tab--active');

    // Switch main collections (only those with multibuy-collection class)
    this.mainCollections.forEach(collection => {
      if (collection.getAttribute('data-collection-content') === targetCollection) {
        collection.classList.add('multibuy-collection--active');
        collection.style.display = 'block';
      } else {
        collection.classList.remove('multibuy-collection--active');
        collection.style.display = 'none';
      }
    });

    // Show/hide secondary sections based on tab
    this.secondarySections.forEach(section => {
      const secondaryFor = section.getAttribute('data-secondary-for');
      if (secondaryFor && secondaryFor.includes(targetCollection)) {
        section.style.display = 'block';
      } else {
        section.style.display = 'none';
      }
    });
  }

  initTagFilters() {
    const filterContainer = this.querySelector('.multibuy-tag-filters');
    if (!filterContainer) return;

    const filters = filterContainer.querySelectorAll('.multibuy-tag-filter');
    const prevBtn = filterContainer.querySelector('.multibuy-tag-filters__prev');
    const nextBtn = filterContainer.querySelector('.multibuy-tag-filters__next');
    const track = filterContainer.querySelector('.multibuy-tag-filters__track');
    const container = filterContainer.querySelector('.multibuy-tag-filters__container');

    let currentScroll = 0;

    // Filter click handler
    filters.forEach(filter => {
      filter.addEventListener('click', () => {
        const selectedTag = filter.dataset.filter;
        
        // Update active state
        filters.forEach(f => f.classList.remove('multibuy-tag-filter--active'));
        filter.classList.add('multibuy-tag-filter--active');
        
        // Scroll clicked filter into view
        const isMobile = window.innerWidth < 1000;
        
        if (isMobile && container) {
          // Mobile: use native scroll
          filter.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        } else if (track && container) {
          // Desktop: use transform
          const filterRect = filter.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          
          // Check if filter is bleeding off the right edge
          if (filterRect.right > containerRect.right) {
            const scrollAmount = filterRect.right - containerRect.right + 20; // 20px padding
            currentScroll += scrollAmount;
            track.style.transform = `translateX(-${currentScroll}px)`;
          }
          // Check if filter is bleeding off the left edge
          else if (filterRect.left < containerRect.left) {
            const scrollAmount = containerRect.left - filterRect.left + 20; // 20px padding
            currentScroll = Math.max(0, currentScroll - scrollAmount);
            track.style.transform = `translateX(-${currentScroll}px)`;
          }
          
          // Update button states if they exist
          if (prevBtn && nextBtn) {
            const maxScroll = track.scrollWidth - container.clientWidth;
            const hasOverflow = track.scrollWidth > container.clientWidth;
            
            if (hasOverflow) {
              prevBtn.disabled = currentScroll <= 0;
              nextBtn.disabled = currentScroll >= maxScroll;
            }
          }
        }
        
        // Filter products
        this.filterProductsByTag(selectedTag);
      });
    });

    // Scroll buttons (desktop only)
    if (prevBtn && nextBtn && track && container) {
      const updateButtons = () => {
        const maxScroll = track.scrollWidth - container.clientWidth;
        const hasOverflow = track.scrollWidth > container.clientWidth;
        
        // Show/hide arrows based on overflow
        if (hasOverflow) {
          prevBtn.style.display = 'flex';
          nextBtn.style.display = 'flex';
          prevBtn.disabled = currentScroll <= 0;
          nextBtn.disabled = currentScroll >= maxScroll;
        } else {
          prevBtn.style.display = 'none';
          nextBtn.style.display = 'none';
        }
      };

      prevBtn.addEventListener('click', () => {
        currentScroll = Math.max(0, currentScroll - 200);
        track.style.transform = `translateX(-${currentScroll}px)`;
        updateButtons();
      });

      nextBtn.addEventListener('click', () => {
        const maxScroll = track.scrollWidth - container.clientWidth;
        currentScroll = Math.min(maxScroll, currentScroll + 200);
        track.style.transform = `translateX(-${currentScroll}px)`;
        updateButtons();
      });

      // Initial button state
      updateButtons();
      
      // Update on resize
      window.addEventListener('resize', () => {
        currentScroll = 0;
        track.style.transform = 'translateX(0)';
        updateButtons();
      });
    }
  }

  filterProductsByTag(tag) {
    const secondarySection = this.querySelector('.multibuy-section--secondary');
    if (!secondarySection) return;
    
    const allCards = secondarySection.querySelectorAll('.product-card');
    const productList = secondarySection.querySelector('.product-list');
    const hiddenProducts = secondarySection.querySelector('.multibuy-hidden-products');
    
    let visibleCount = 0;
    let totalMatchingCount = 0;
    const matchingCards = [];
    
    allCards.forEach(card => {
      let shouldShow = false;
      
      if (tag === 'all') {
        shouldShow = true;
        totalMatchingCount++;
      } else {
        const productTags = card.dataset.productTags || '';
        const tagsArray = productTags.split(',').map(t => t.trim());
        
        if (tagsArray.includes(tag.toLowerCase())) {
          shouldShow = true;
          totalMatchingCount++;
        }
      }
      
      if (shouldShow) {
        card.style.display = '';
        matchingCards.push(card);
      } else {
        card.style.display = 'none';
      }
    });
    
    // Move matching cards from hidden container to visible list (up to 12 total)
    if (productList && hiddenProducts) {
      const currentVisibleCards = Array.from(productList.querySelectorAll('.product-card')).filter(c => c.style.display !== 'none');
      visibleCount = currentVisibleCards.length;
      
      // If we have less than 12 visible matching cards, move more from hidden
      if (visibleCount < 12) {
        const hiddenMatchingCards = matchingCards.filter(card => hiddenProducts.contains(card));
        const cardsToMove = hiddenMatchingCards.slice(0, 12 - visibleCount);
        
        cardsToMove.forEach(card => {
          productList.appendChild(card);
          // Attach listeners to newly shown cards
          this.attachProductCardListeners(card);
          visibleCount++;
        });
      }
    } else if (productList) {
      visibleCount = Array.from(productList.querySelectorAll('.product-card')).filter(c => c.style.display !== 'none').length;
    }
    
    // Update the load more text and button
    const loadMoreContainer = secondarySection.querySelector('.multibuy-load-more');
    if (loadMoreContainer) {
      const text = loadMoreContainer.querySelector('.multibuy-load-more__text');
      const button = loadMoreContainer.querySelector('.multibuy-load-more__button');
      const showingTextTemplate = loadMoreContainer.dataset.showingText;
      
      if (text && showingTextTemplate) {
        const updatedText = showingTextTemplate
          .replace('{{ current }}', visibleCount)
          .replace('{{ total }}', totalMatchingCount);
        text.textContent = updatedText;
      }
      
      // Show/hide load more button based on remaining filtered products
      if (button) {
        const remainingVisible = hiddenProducts ? 
          Array.from(hiddenProducts.querySelectorAll('.product-card')).filter(c => c.style.display !== 'none').length : 
          0;
        
        if (remainingVisible > 0) {
          button.style.display = '';
          button.dataset.total = totalMatchingCount;
        } else {
          button.style.display = 'none';
        }
      }
    }
  }

  updateVariantAvailability(modal) {
    const variantsScript = modal.querySelector('script[data-variants]');
    if (!variantsScript) return;
    
    const variants = JSON.parse(variantsScript.textContent);
    const optionsContainer = modal.querySelector('.multibuy-variant-modal__options');
    const selectedOptions = [];
    
    // Get selected values from radio buttons (color swatches)
    const optionInputs = optionsContainer.querySelectorAll('input[type="radio"]:checked');
    optionInputs.forEach(input => {
      const position = parseInt(input.dataset.optionPosition);
      selectedOptions[position] = input.value;
    });
    
    // Get selected values from dropdowns
    const optionDropdowns = optionsContainer.querySelectorAll('.multibuy-variant-dropdown__select');
    optionDropdowns.forEach(select => {
      const position = parseInt(select.dataset.optionPosition);
      selectedOptions[position] = select.value;
    });
    
    // Find matching variant
    const matchingVariant = variants.find(variant => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
    
    // Enable/disable add to basket button. When options are still missing, keep the button
    // clickable (but styled as inactive) so clicking it can show the selection error.
    const addButton = modal.querySelector('[data-multibuy-add-to-basket]');
    const missingOptions = this.getMissingOptions(modal);

    if (missingOptions.length === 0) {
      this.hideSelectionError(modal);
    }

    if (addButton) {
      addButton.classList.toggle('is-incomplete', missingOptions.length > 0);
      addButton.setAttribute('aria-disabled', missingOptions.length > 0 ? 'true' : 'false');

      if (missingOptions.length > 0) {
        addButton.disabled = false;
      } else if (matchingVariant && matchingVariant.available) {
        addButton.disabled = false;
      } else {
        addButton.disabled = true;
      }
    }
  }

  getMissingOptions(modal) {
    const missing = [];

    modal.querySelectorAll('.multibuy-variant-option').forEach(optionDiv => {
      const radios = optionDiv.querySelectorAll('input[type="radio"]');
      const select = optionDiv.querySelector('select');
      const isMissing = radios.length > 0
        ? !optionDiv.querySelector('input[type="radio"]:checked')
        : (select && !select.value);

      if (isMissing) {
        const label = optionDiv.querySelector('.multibuy-variant-option__label');
        missing.push(label ? label.textContent.trim() : '');
      }
    });

    return missing;
  }

  showSelectionError(modal, missingOptions) {
    const strings = window.themeVariables?.strings || {};
    const names = missingOptions.map(name => name.toLowerCase());
    const message = names.length > 1
      ? (strings.forceSelectionMultiple || 'Selecteer [OPTIONS] om verder te gaan').replace('[OPTIONS]', names.join(', '))
      : (strings.forceSelectionSingle || 'Selecteer [OPTION] om verder te gaan').replace('[OPTION]', names[0]);

    const addButton = modal.querySelector('[data-multibuy-add-to-basket]');
    if (!addButton) return;

    let banner = modal.querySelector('.multibuy-variant-modal__error');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'banner banner--error multibuy-variant-modal__error';
      banner.setAttribute('role', 'alert');
      addButton.insertAdjacentElement('beforebegin', banner);
    }

    banner.innerHTML = `<svg width="20" height="20" fill="none" viewBox="0 0 13 13" aria-hidden="true">
        <circle cx="6.5" cy="6.5" r="6.5" fill="currentColor"/>
        <path fill="#fff" d="M6.75 7.97a.387.387 0 0 1-.3-.12.606.606 0 0 1-.12-.34l-.3-3.82c-.02-.247.033-.443.16-.59.127-.153.313-.23.56-.23.24 0 .42.077.54.23.127.147.18.343.16.59l-.3 3.82a.522.522 0 0 1-.12.34.344.344 0 0 1-.28.12Zm0 2.08a.744.744 0 0 1-.55-.21.751.751 0 0 1-.2-.54c0-.213.067-.387.2-.52.14-.14.323-.21.55-.21.233 0 .413.07.54.21.133.133.2.307.2.52 0 .22-.067.4-.2.54-.127.14-.307.21-.54.21Z"/>
      </svg>`;
    const text = document.createElement('span');
    text.textContent = message;
    banner.appendChild(text);
  }

  hideSelectionError(modal) {
    modal.querySelector('.multibuy-variant-modal__error')?.remove();
  }

  initQuickBuyIntercept() {
    const self = this;
    
    // Close modal when clicking overlay
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('modal__overlay') && e.target.closest('.multibuy-variant-modal')) {
        const modal = e.target.closest('.multibuy-variant-modal');
        if (modal && typeof modal.hide === 'function') {
          modal.hide();
        }
        return;
      }
    });
    
    // Open custom multibuy modal
    document.addEventListener('click', (e) => {
      const modalTrigger = e.target.closest('[data-multibuy-modal-trigger]');
      if (modalTrigger) {
        e.preventDefault();
        e.stopPropagation();
        
        const modalId = modalTrigger.getAttribute('aria-controls');
        const modal = document.getElementById(modalId);
        
        if (modal && typeof modal.show === 'function') {
          self.hideSelectionError(modal);
          modal.show();
          // Check variant availability after modal opens
          setTimeout(() => self.updateVariantAvailability(modal), 50);
        }
        return;
      }
      
      // Listen for variant option changes (radio buttons)
      const variantInput = e.target.closest('.multibuy-variant-swatch__input');
      if (variantInput) {
        const modal = variantInput.closest('.multibuy-variant-modal');
        if (modal) {
          setTimeout(() => self.updateVariantAvailability(modal), 10);
        }
        return;
      }
      
      // Listen for variant option changes (dropdowns)
      const variantDropdown = e.target.closest('.multibuy-variant-dropdown__select');
      if (variantDropdown) {
        const modal = variantDropdown.closest('.multibuy-variant-modal');
        if (modal) {
          setTimeout(() => self.updateVariantAvailability(modal), 10);
        }
        return;
      }
      
      // Prevent clicks on modal content from triggering card selection
      if (e.target.closest('.multibuy-variant-modal__content')) {
        // Only handle if it's actually the add to basket button
        const addToBasketBtn = e.target.closest('[data-multibuy-add-to-basket]');
        if (!addToBasketBtn) {
          return; // Ignore other clicks inside modal
        }
      }
      
      // Handle "Add to basket" button in custom modal
      const addToBasketBtn = e.target.closest('[data-multibuy-add-to-basket]');
      if (addToBasketBtn) {
        e.preventDefault();
        e.stopPropagation();
        
        const modal = addToBasketBtn.closest('.multibuy-variant-modal');
        if (!modal) return;

        const missingOptions = self.getMissingOptions(modal);
        if (missingOptions.length > 0) {
          self.showSelectionError(modal, missingOptions);
          return;
        }

        const modalId = modal.id;
        const quickBuyButton = document.querySelector(`[aria-controls="${modalId}"]`);
        if (!quickBuyButton) return;
        
        const productCard = quickBuyButton.closest('.product-card--selectable');
        if (!productCard) return;
        
        const productId = productCard.getAttribute('data-product-id');
        const selectionType = productCard.getAttribute('data-selection-type');
        
        // Get selected options
        const optionsContainer = modal.querySelector('.multibuy-variant-modal__options');
        const selectedOptions = [];
        
        // Get values from radio buttons (color swatches)
        const optionInputs = optionsContainer.querySelectorAll('input[type="radio"]:checked');
        optionInputs.forEach(input => {
          const position = parseInt(input.dataset.optionPosition);
          selectedOptions[position] = input.value;
        });
        
        // Get values from dropdowns
        const optionDropdowns = optionsContainer.querySelectorAll('.multibuy-variant-dropdown__select');
        optionDropdowns.forEach(select => {
          const position = parseInt(select.dataset.optionPosition);
          selectedOptions[position] = select.value;
        });
                
        // Get variants from embedded JSON
        const variantsScript = modal.querySelector('script[data-variants]');
        if (!variantsScript) {
          console.error('No variants data found');
          return;
        }
        
        const variants = JSON.parse(variantsScript.textContent);
        
        // Find matching variant
        const matchingVariant = variants.find(variant => {
          const matches = variant.options.every((option, index) => {
            const match = option === selectedOptions[index];
            return match;
          });
          return matches;
        });
                
        if (!matchingVariant) {
          console.error('No matching variant found');
          console.error('Selected options:', selectedOptions);
          console.error('Available variants:', variants.map(v => ({ title: v.title, options: v.options })));
          return;
        }
        
        const selectedVariantId = matchingVariant.id;
        const variantPrice = matchingVariant.price;
        
        // Update product card
        productCard.setAttribute('data-variant-id', selectedVariantId);
        productCard.setAttribute('data-product-price', variantPrice);
        productCard.dataset.skipQuickBuy = 'true';
        
        // Get variant info from the matched variant
        const variantTitle = matchingVariant.title || matchingVariant.name || '';
        
        // Get all option names and values
        const variantOptions = [];
        const optionNames = [];
        optionsContainer.querySelectorAll('.multibuy-variant-option').forEach((optionDiv, index) => {
          const label = optionDiv.querySelector('.multibuy-variant-option__label');
          if (label) {
            const optionName = label.textContent.split(':')[0].trim();
            optionNames.push(optionName);
            if (selectedOptions[index]) {
              variantOptions.push({
                name: optionName,
                value: selectedOptions[index]
              });
            }
          }
        });
        
        // Get variant image - use featured_image if available, otherwise fallback to card image
        let variantImage = matchingVariant.featured_image?.src || matchingVariant.featured_image;
        if (!variantImage) {
          variantImage = self.getCurrentImage(productCard);
        }
        
        // Select the product based on selection type
        if (selectionType === 'single') {
          // Main product selection
          const mainCards = self.querySelectorAll('.product-card--selectable[data-selection-type="single"]');
          mainCards.forEach(c => c.classList.remove('product-card--selected'));
          
          productCard.classList.add('product-card--selected');
          
          self.selectedMainProduct = {
            id: productId,
            title: productCard.getAttribute('data-product-title'),
            price: variantPrice,
            image: variantImage,
            variantId: selectedVariantId,
            variant: variantTitle,
            variantOptions: variantOptions
          };
          
          self.saveSelections();
          self.updateMiniBasket();
          self.updateSecondaryCardsState();
          
          // Scroll to secondary section
          setTimeout(() => {
            const secondarySection = self.querySelector('.multibuy-section--secondary');
            if (secondarySection) {
              secondarySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 300);
        } else {
          // Secondary product selection
          if (productCard.classList.contains('product-card--selected')) {
            const existingProduct = self.selectedSecondaryProducts.get(productId);
            if (existingProduct) {
              existingProduct.variantId = selectedVariantId;
              existingProduct.price = variantPrice;
              existingProduct.variant = variantTitle;
              existingProduct.variantOptions = variantOptions;
              existingProduct.image = variantImage;
              self.saveSelections();
              self.updateMiniBasket();
            }
          } else {
            productCard.classList.add('product-card--selected');
                      
            self.selectedSecondaryProducts.set(productId, {
              id: productId,
              title: productCard.getAttribute('data-product-title'),
              price: variantPrice,
              image: variantImage,
              variantId: selectedVariantId,
              variant: variantTitle,
              variantOptions: variantOptions,
              quantity: 1
            });
            
            const qtyValue = productCard.querySelector('.product-card__quantity-value');
            if (qtyValue) {
              qtyValue.textContent = '1';
            }
            
            self.saveSelections();
            self.updateMiniBasket();
          }
        }
        
        setTimeout(() => {
          delete productCard.dataset.skipQuickBuy;
        }, 100);
        
        // Close modal
        if (typeof modal.hide === 'function') {
          modal.hide();
        }
        
        return;
      }
      
      const quickBuyButton = e.target.closest('.product-card__quick-add-button');
      if (!quickBuyButton) return;
      
      // Check if this is a multibuy product card
      const productCard = quickBuyButton.closest('.product-card--selectable');
      if (!productCard) return;
      
      const selectionType = productCard.getAttribute('data-selection-type');
      
      // Only intercept for secondary (multiple) products
      if (selectionType !== 'multiple') return;
      
      // Check if main product is selected
      if (!this.selectedMainProduct) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      
      // Find the modal
      const modalId = quickBuyButton.getAttribute('aria-controls');
      if (!modalId) return;
      
      // Wait for modal to open and intercept its add to cart
      setTimeout(() => {
        const modal = document.getElementById(modalId);
        if (!modal) return;
        
        // Find the add to cart button in the modal
        const addToCartButton = modal.querySelector('button[type="submit"][name="add"]');
        if (!addToCartButton) return;
        
        // Replace the add to cart behavior
        const newButton = addToCartButton.cloneNode(true);
        addToCartButton.parentNode.replaceChild(newButton, addToCartButton);
        
        newButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Get selected variant from modal
          const variantSelect = modal.querySelector('select[name="id"]');
          const variantRadio = modal.querySelector('input[name="id"]:checked');
          const selectedVariantId = variantSelect ? variantSelect.value : (variantRadio ? variantRadio.value : null);
          
          if (!selectedVariantId) return;
          
          // Update the product card's variant
          productCard.setAttribute('data-variant-id', selectedVariantId);
          
          // Trigger selection on the product card
          productCard.click();
          
          // Close the modal
          if (typeof modal.hide === 'function') {
            modal.hide();
          } else {
            modal.removeAttribute('open');
          }
        });
      }, 100);
    });
  }

  initLoadMore() {
    const loadMoreButtons = this.querySelectorAll('.multibuy-load-more__button');
    
    loadMoreButtons.forEach(button => {
      button.addEventListener('click', () => {
        const collectionId = button.dataset.collection;
        const total = parseInt(button.dataset.total);
        
        // Find the parent section
        const section = button.closest('.multibuy-section, .multibuy-collection');
        if (!section) return;
        
        // Find hidden products container
        const hiddenProducts = section.querySelector('.multibuy-hidden-products');
        if (!hiddenProducts) return;
        
        // Get the product list
        const productList = section.querySelector('.product-list');
        if (!productList) return;
        
        // Get current visible count (only non-filtered cards)
        const currentVisible = Array.from(productList.querySelectorAll('.product-card'))
          .filter(card => card.style.display !== 'none').length;
        
        // Load next 12 products (only non-filtered)
        const hiddenCards = Array.from(hiddenProducts.querySelectorAll('.product-card'))
          .filter(card => card.style.display !== 'none');
        const cardsToShow = hiddenCards.slice(0, 12);
        
        cardsToShow.forEach(card => {
          productList.appendChild(card);
          // Attach listeners to newly shown cards
          this.attachProductCardListeners(card);
        });
        
        // Update count
        const newVisible = currentVisible + cardsToShow.length;
        
        // Update text with locale
        const loadMoreContainer = button.closest('.multibuy-load-more');
        const text = loadMoreContainer.querySelector('.multibuy-load-more__text');
        const showingTextTemplate = loadMoreContainer.dataset.showingText;
        
        if (text && showingTextTemplate) {
          // Replace placeholders with actual values
          const updatedText = showingTextTemplate
            .replace('{{ current }}', newVisible)
            .replace('{{ total }}', total);
          text.textContent = updatedText;
        }
        
        // Check if there are more products to load (only non-filtered)
        const remainingCards = Array.from(hiddenProducts.querySelectorAll('.product-card'))
          .filter(card => card.style.display !== 'none').length;
        if (remainingCards === 0) {
          // No more products, hide button
          button.style.display = 'none';
          if (hiddenProducts.querySelectorAll('.product-card').length === 0) {
            hiddenProducts.remove();
          }
        }
      });
    });
  }

  handleSwatchChange(e, card) {
    e.stopPropagation();
    
    const input = e.currentTarget;
    if (!input.hasAttribute('data-variant-media')) return;
    
    try {
      const variantMedia = JSON.parse(input.getAttribute('data-variant-media'));
      const mediaId = variantMedia.id;
      const carousel = card.querySelector('.product-card__carousel');
      
      if (!carousel) return;
      
      // Find and scroll to the matching slide
      const slides = carousel.querySelectorAll('[data-media-id]');
      let variantImage = null;
      
      slides.forEach((slide) => {
        if (slide.getAttribute('data-media-id') == mediaId) {
          carousel.scrollTo({
            left: slide.offsetLeft,
            behavior: 'smooth'
          });
          
          const img = slide.querySelector('img');
          if (img) {
            variantImage = img.currentSrc || img.src;
          }
        }
      });

      // Update selected product if this card is selected
      if (card.classList.contains('product-card--selected')) {
        const selectionType = card.getAttribute('data-selection-type');
        const productId = card.getAttribute('data-product-id');
        const variantData = this.getSelectedVariant(card);
        
        if (selectionType === 'single' && this.selectedMainProduct) {
          this.selectedMainProduct.variant = variantData.variant;
          this.selectedMainProduct.variantColor = variantData.color;
          if (variantImage) {
            this.selectedMainProduct.image = variantImage;
          }
        } else if (selectionType === 'multiple') {
          const product = this.selectedSecondaryProducts.get(productId);
          if (product) {
            product.variant = variantData.variant;
            product.variantColor = variantData.color;
            if (variantImage) {
              product.image = variantImage;
            }
          }
        }
        
        this.updateMiniBasket();
      }
    } catch (err) {
      console.warn('Could not parse variant media:', err);
    }
  }

  handleCardClick(e, card) {
    // Prevent selection if clicking inside a modal
    if (e.target.closest('.multibuy-variant-modal') || e.target.closest('.modal')) {
      return;
    }
    
    // Prevent selection if clicking on interactive elements
    if (e.target.closest('fieldset') || 
        e.target.closest('input[type="radio"]') || 
        e.target.closest('button') ||
        e.target.closest('label')) {
      return;
    }

    const selectionType = card.getAttribute('data-selection-type');
    const productId = card.getAttribute('data-product-id');
    
    // Check if this card has a modal (multi-option product)
    const quickBuyButton = card.querySelector('[data-multibuy-modal-trigger]');
    if (quickBuyButton && !card.dataset.skipQuickBuy) {
      const modalId = quickBuyButton.getAttribute('aria-controls');
      const modal = document.getElementById(modalId);
      
      if (modal && typeof modal.show === 'function') {
        modal.show();
        // Check variant availability after modal opens
        setTimeout(() => this.updateVariantAvailability(modal), 50);
      }
      return;
    }
    
    // Prevent secondary selection if no main product is selected
    if (selectionType === 'multiple' && !this.selectedMainProduct) {
      return;
    }

    if (selectionType === 'single') {
      // Single selection: deselect all main cards, select this one
      const mainCards = this.querySelectorAll('.product-card--selectable[data-selection-type="single"]');
      mainCards.forEach(c => c.classList.remove('product-card--selected'));
      
      card.classList.add('product-card--selected');

      // Get current image and variant
      const currentImage = this.getCurrentImage(card);
      const variantData = this.getSelectedVariant(card);

      // Get the selected variant's price
      const selectedVariantId = card.getAttribute('data-variant-id');
      let variantPrice = card.getAttribute('data-product-price');
      
      // Try to get the actual variant price from the variant data
      if (variantData.variant && variantData.variant.price) {
        variantPrice = variantData.variant.price;
      }
      
      // Update main selected product
      this.selectedMainProduct = {
        id: productId,
        title: card.getAttribute('data-product-title'),
        price: variantPrice,
        image: currentImage,
        variantId: selectedVariantId,
        variant: variantData.variant,
        variantColor: variantData.color
      };

      // Determine which collection this main product belongs to
      const parentCollection = card.closest('.multibuy-collection');
      const collectionType = parentCollection ? parentCollection.getAttribute('data-collection-content') : null;

      // Remove incompatible secondary products
      if (collectionType) {
        const productsToRemove = [];
        
        this.selectedSecondaryProducts.forEach((product, secondaryProductId) => {
          const secondaryCard = this.querySelector(`[data-product-id="${secondaryProductId}"]`);
          if (!secondaryCard) return;
          
          const section = secondaryCard.closest('.multibuy-section--secondary');
          if (!section) return;
          
          const secondaryFor = section.getAttribute('data-secondary-for');
          if (secondaryFor && !secondaryFor.includes(collectionType)) {
            // Mark for removal
            productsToRemove.push(secondaryProductId);
            
            // Remove visual selection
            secondaryCard.classList.remove('product-card--selected');
            
            // Reset quantity display
            const qtyValue = secondaryCard.querySelector('.product-card__quantity-value');
            if (qtyValue) qtyValue.textContent = '1';
          }
        });
        
        // Remove incompatible products from the map
        productsToRemove.forEach(id => this.selectedSecondaryProducts.delete(id));
      }
    } else if (selectionType === 'multiple') {
      // Multiple selection: toggle selection
      if (card.classList.contains('product-card--selected')) {
        // Deselect and remove from map
        card.classList.remove('product-card--selected');
        this.selectedSecondaryProducts.delete(productId);
      } else {
        // Select and add to map
        card.classList.add('product-card--selected');
        
        const currentImage = this.getCurrentImage(card);
        const variantData = this.getSelectedVariant(card);
        
        // Get the selected variant's price
        const selectedVariantId = card.getAttribute('data-variant-id');
        let variantPrice = card.getAttribute('data-product-price');
        
        // Try to get the actual variant price from the variant data
        if (variantData.variant && variantData.variant.price) {
          variantPrice = variantData.variant.price;
        }
        
        this.selectedSecondaryProducts.set(productId, {
          id: productId,
          title: card.getAttribute('data-product-title'),
          price: variantPrice,
          image: currentImage,
          variantId: selectedVariantId,
          variant: variantData.variant,
          variantColor: variantData.color,
          quantity: 1
        });

        // Update quantity display
        const qtyValue = card.querySelector('.product-card__quantity-value');
        if (qtyValue) qtyValue.textContent = '1';
      }
    }

    this.saveSelections();
    this.updateMiniBasket();
    this.updateSecondaryCardsState();
    
    // Scroll to section 2 when main product is selected
    if (selectionType === 'single') {
      this.scrollToNextSection();
    }
  }

  updateSecondaryCardsState() {
    // Enable/disable secondary cards based on whether main product is selected
    const secondaryCards = this.querySelectorAll('.product-card--selectable[data-selection-type="multiple"]');
    const hasMainProduct = this.selectedMainProduct !== null;
    
    secondaryCards.forEach(card => {
      if (hasMainProduct) {
        card.classList.remove('product-card--disabled');
      } else {
        card.classList.add('product-card--disabled');
      }
    });
  }

  scrollToNextSection() {
    // Find the first secondary section
    const secondarySection = this.querySelector('.multibuy-section--secondary');
    if (secondarySection) {
      // Smooth scroll to the section
      secondarySection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  getCurrentImage(card) {
    const carousel = card.querySelector('.product-card__carousel');
    let currentImage = card.getAttribute('data-product-image');
    
    if (!carousel) return currentImage;
    
    // Check if there's a selected swatch with variant media
    const selectedSwatch = card.querySelector('.product-card__info [type="radio"]:checked[data-variant-media]');
    
    if (selectedSwatch) {
      try {
        const variantMedia = JSON.parse(selectedSwatch.getAttribute('data-variant-media'));
        const mediaId = variantMedia.id;
        const targetSlide = carousel.querySelector(`[data-media-id="${mediaId}"]`);
        
        if (targetSlide) {
          const img = targetSlide.querySelector('img');
          if (img) {
            return img.currentSrc || img.src;
          }
        }
      } catch (err) {
        console.warn('Could not get variant media:', err);
      }
    }
    
    // Fallback to visible slide
    const visibleSlide = carousel.querySelector('.product-card__media.is-initial') || 
                        carousel.querySelector('.product-card__media');
    
    if (visibleSlide) {
      const img = visibleSlide.querySelector('img');
      if (img) {
        return img.currentSrc || img.src;
      }
    }
    
    return currentImage;
  }

  getSelectedVariant(card) {
    const selectedSwatches = card.querySelectorAll('.product-card__info [type="radio"]:checked');
    const variant = {};
    let color = '';
    
    selectedSwatches.forEach(swatch => {
      const fieldset = swatch.closest('fieldset');
      const legend = fieldset ? fieldset.querySelector('legend') : null;
      const optionName = legend ? legend.textContent.trim() : '';
      
      // Get the label associated with this radio button
      const label = document.querySelector(`label[for="${swatch.id}"]`);
      let labelText = '';
      
      if (label) {
        // Try to get text from data-tooltip attribute first
        labelText = label.getAttribute('data-tooltip') || '';
        
        // If no tooltip, try to get from sr-only span
        if (!labelText) {
          const srOnly = label.querySelector('.sr-only');
          labelText = srOnly ? srOnly.textContent.trim() : '';
        }
        
        // If still no text, get from label text content
        if (!labelText) {
          labelText = label.textContent.trim();
        }
      }
      
      if (optionName && labelText) {
        variant[optionName] = labelText;
        
        // Check if this is a color option
        const nameLower = optionName.toLowerCase();
        if (nameLower.includes('color') || nameLower.includes('colour') || nameLower.includes('kleur')) {
          color = labelText;
        }
      }
    });
    
    return {
      variant: variant,
      color: color
    };
  }

  toggleMobileBasket() {
    if (this.miniBasketMobile) {
      const isCollapsed = this.miniBasketMobile.classList.toggle('is-collapsed');
      
      // Prevent body scroll when basket is open (not collapsed)
      if (isCollapsed) {
        document.body.style.overflow = '';
      } else {
        document.body.style.overflow = 'hidden';
      }
    }
  }

  handleQuantityChange(card, delta) {
    const productId = card.getAttribute('data-product-id');
    const product = this.selectedSecondaryProducts.get(productId);
    
    if (!product) return;
    
    const newQuantity = Math.max(1, product.quantity + delta);
    product.quantity = newQuantity;
    
    // Update display
    const qtyValue = card.querySelector('.product-card__quantity-value');
    if (qtyValue) {
      qtyValue.textContent = newQuantity;
    }
    
    // Update decrease button state
    const decreaseBtn = card.querySelector('.product-card__quantity-decrease');
    if (decreaseBtn) {
      decreaseBtn.disabled = newQuantity <= 1;
    }
    
    this.saveSelections();
    this.updateMiniBasket();
  }

  handleRemoveProduct(removeBtn) {
    // Find the product container
    const productContainer = removeBtn.closest('.multibuy-mini-basket__product');
    if (!productContainer) return;

    // Check if this is in the main section or secondary section
    const isMainProduct = productContainer.closest('.multibuy-mini-basket__section:not(.multibuy-mini-basket__section--secondary)');
    const isSecondaryProduct = productContainer.closest('.multibuy-mini-basket__section--secondary');

    if (isMainProduct) {
      // Remove main product
      if (this.selectedMainProduct) {
        const productId = this.selectedMainProduct.id;
        
        // Find and deselect the card
        const card = this.querySelector(`[data-product-id="${productId}"]`);
        if (card) {
          card.classList.remove('product-card--selected');
        }
        
        // Clear selection
        this.selectedMainProduct = null;
      }
    } else if (isSecondaryProduct) {
      // For secondary products, we need to find which product this is
      // The product container is dynamically created, so we need to identify it by its content
      const titleElement = productContainer.querySelector('.multibuy-mini-basket__product-title');
      if (titleElement) {
        const productTitle = titleElement.textContent;
        
        // Find the matching product in our map
        let productIdToRemove = null;
        this.selectedSecondaryProducts.forEach((product, productId) => {
          if (product.title === productTitle) {
            productIdToRemove = productId;
          }
        });
        
        if (productIdToRemove) {
          // Remove from map
          this.selectedSecondaryProducts.delete(productIdToRemove);
          
          // Find and deselect the card
          const card = this.querySelector(`[data-product-id="${productIdToRemove}"]`);
          if (card) {
            card.classList.remove('product-card--selected');
            
            // Reset quantity display
            const qtyValue = card.querySelector('.product-card__quantity-value');
            if (qtyValue) qtyValue.textContent = '1';
          }
        }
      }
    }

    // Save and update
    this.saveSelections();
    this.updateMiniBasket();
    this.updateSecondaryCardsState();
  }

  async addToCart() {
    const items = [];
    
    // Add main product
    if (this.selectedMainProduct) {
      items.push({
        id: this.selectedMainProduct.variantId,
        quantity: 1
      });
    }
    
    // Add secondary products with quantities
    this.selectedSecondaryProducts.forEach(product => {
      items.push({
        id: product.variantId,
        quantity: product.quantity
      });
    });
    
    if (items.length === 0) return;
    
    // Disable buttons during add
    this.addButtons.forEach(btn => {
      btn.disabled = true;
      btn.textContent = 'Adding...';
    });
    
    try {
      // Add items to cart using Shopify Cart API
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add to cart');
      }
      
      const responseJson = await response.json();
      
      // Get updated cart data
      const cartData = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      
      // Update cart count in header
      const cartCountElements = document.querySelectorAll('.header__cart-count, [data-cart-count]');
      cartCountElements.forEach(el => {
        el.textContent = cartData.item_count;
        if (cartData.item_count > 0) {
          el.style.display = '';
        }
      });
      
      // Dispatch cart update event for theme to handle
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cartData }
      }));
      
      // Try to open cart drawer and refresh it
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Refresh cart drawer content
        if (typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(cartData);
        }
        // Open the drawer
        if (typeof cartDrawer.show === 'function') {
          cartDrawer.show();
        }
      }
      
      // Clear selection after successful add
      this.resetSelection();
      
      // Close mobile basket if open
      if (this.miniBasketMobile && !this.miniBasketMobile.classList.contains('is-collapsed')) {
        this.miniBasketMobile.classList.add('is-collapsed');
        document.body.style.overflow = '';
      }
      
      // Show success feedback
      this.addButtons.forEach(btn => {
        btn.textContent = 'Added!';
        setTimeout(() => {
          btn.textContent = 'Add to cart';
          btn.disabled = false;
        }, 2000);
      });
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Show error feedback
      this.addButtons.forEach(btn => {
        btn.textContent = 'Error - Try again';
        btn.disabled = false;
        setTimeout(() => {
          btn.textContent = 'Add to cart';
        }, 3000);
      });
    }
  }

  saveSelections() {
    const state = {
      mainProduct: this.selectedMainProduct,
      secondaryProducts: Array.from(this.selectedSecondaryProducts.entries())
    };
    
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch (e) {
      console.warn('Could not save selections to localStorage:', e);
    }
  }

  restoreSelections() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (!saved) return;
      
      const state = JSON.parse(saved);
      
      // Restore main product
      if (state.mainProduct) {
        const card = this.querySelector(`[data-product-id="${state.mainProduct.id}"]`);
        if (card && card.getAttribute('data-selection-type') === 'single') {
          card.classList.add('product-card--selected');
          this.selectedMainProduct = state.mainProduct;
        }
      }
      
      // Restore secondary products
      if (state.secondaryProducts && Array.isArray(state.secondaryProducts)) {
        state.secondaryProducts.forEach(([productId, productData]) => {
          const card = this.querySelector(`[data-product-id="${productId}"]`);
          if (card && card.getAttribute('data-selection-type') === 'multiple') {
            card.classList.add('product-card--selected');
            this.selectedSecondaryProducts.set(productId, productData);
            
            // Update quantity display
            const qtyValue = card.querySelector('.product-card__quantity-value');
            if (qtyValue) {
              qtyValue.textContent = productData.quantity;
            }
          }
        });
      }
    } catch (e) {
      console.warn('Could not restore selections from localStorage:', e);
    }
  }

  resetSelection() {
    this.selectedMainProduct = null;
    this.selectedSecondaryProducts.clear();
    this.productCards.forEach(c => {
      c.classList.remove('product-card--selected');
      // Reset quantity displays
      const qtyValue = c.querySelector('.product-card__quantity-value');
      if (qtyValue) qtyValue.textContent = '1';
    });
    
    // Clear localStorage
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      console.warn('Could not clear localStorage:', e);
    }
    
    this.updateMiniBasket();
    this.updateSecondaryCardsState();
  }

  formatPrice(cents) {
    const euros = (cents / 100).toFixed(2);
    return '€ ' + euros.replace('.', ',');
  }

  updateMiniBasket() {
    const hasMainProduct = this.selectedMainProduct !== null;
    const hasSecondaryProducts = this.selectedSecondaryProducts.size > 0;
    
    // Get secondary products container
    const secondaryContainers = this.querySelectorAll('.multibuy-mini-basket__secondary-products');
    const secondaryEmptyStates = this.querySelectorAll('.multibuy-mini-basket__empty--secondary');
    
    if (!hasMainProduct && !hasSecondaryProducts) {
      // Show empty states
      this.emptyStates.forEach(el => el.style.display = 'flex');
      this.productStates.forEach(el => el.style.display = 'none');
      secondaryEmptyStates.forEach(el => el.style.display = 'flex');
      secondaryContainers.forEach(el => el.style.display = 'none');
      this.totalPrices.forEach(el => el.textContent = '€ 0');
      this.addButtons.forEach(btn => {
        btn.disabled = true;
        btn.textContent = this.locale?.addButton || 'Add to cart';
      });
      
      // Reset price breakdown to empty state
      this.updatePriceBreakdown(0);
    } else {
      // Calculate total
      let total = 0;
      
      // Update main product
      if (hasMainProduct) {
        const mainPrice = parseInt(this.selectedMainProduct.price, 10) || 0;
        total += mainPrice;
        
        // Show main product, hide main empty state
        const mainEmptyStates = this.querySelectorAll('.multibuy-mini-basket__empty:not(.multibuy-mini-basket__empty--secondary)');
        mainEmptyStates.forEach(el => el.style.display = 'none');
        this.productStates.forEach(el => el.style.display = 'flex');
        
        this.images.forEach(img => {
          img.src = this.selectedMainProduct.image;
          img.alt = this.selectedMainProduct.title;
        });
        
        this.titles.forEach(el => el.textContent = this.selectedMainProduct.title);
        
        let variantHtml = '';
        if (this.selectedMainProduct.variantOptions && this.selectedMainProduct.variantOptions.length > 0) {
          variantHtml = this.selectedMainProduct.variantOptions
            .map(opt => `${opt.name}: ${opt.value}`)
            .join('<br>');
        } else if (this.selectedMainProduct.variantColor) {
          variantHtml = `Color: ${this.selectedMainProduct.variantColor}`;
        }
        this.variants.forEach(el => el.innerHTML = variantHtml);
        
        this.prices.forEach(el => el.textContent = this.formatPrice(this.selectedMainProduct.price));
      } else {
        // No main product selected
        const mainEmptyStates = this.querySelectorAll('.multibuy-mini-basket__empty:not(.multibuy-mini-basket__empty--secondary)');
        mainEmptyStates.forEach(el => el.style.display = 'flex');
        this.productStates.forEach(el => el.style.display = 'none');
      }
      
      // Calculate secondary products total ONCE (not per container)
      if (hasSecondaryProducts) {
        this.selectedSecondaryProducts.forEach(product => {
          const productPrice = parseInt(product.price, 10) || 0;
          const productQuantity = parseInt(product.quantity, 10) || 1;
          const lineTotal = productPrice * productQuantity;
          total += lineTotal;
        });
      }
      
      // Calculate free charms (cheapest ones)
      const CHARMS_FOR_FREE = 3;
      const totalCharms = Array.from(this.selectedSecondaryProducts.values())
        .reduce((sum, product) => sum + product.quantity, 0);
      const freeCharmsCount = Math.floor(totalCharms / CHARMS_FOR_FREE);
      
      // Sort products by price (cheapest first) and mark free ones
      let freeCharmsRemaining = freeCharmsCount;
      const productsWithFreeStatus = Array.from(this.selectedSecondaryProducts.values())
        .sort((a, b) => parseInt(a.price, 10) - parseInt(b.price, 10))
        .map(product => {
          const productPrice = parseInt(product.price, 10) || 0;
          const productQuantity = parseInt(product.quantity, 10) || 1;
          let freeQuantity = 0;
          
          if (freeCharmsRemaining > 0) {
            freeQuantity = Math.min(productQuantity, freeCharmsRemaining);
            freeCharmsRemaining -= freeQuantity;
          }
          
          return {
            ...product,
            price: productPrice,
            quantity: productQuantity,
            freeQuantity: freeQuantity,
            paidQuantity: productQuantity - freeQuantity
          };
        });
      
      // Update secondary products UI
      if (hasSecondaryProducts) {
        secondaryEmptyStates.forEach(el => el.style.display = 'none');
        secondaryContainers.forEach(container => {
          container.style.display = 'block';
          container.innerHTML = '';
          
          productsWithFreeStatus.forEach(product => {
            // Determine price display
            let priceDisplay = '';
            const freeLabel = this.locale?.freeLabel || 'GRATIS';
            
            if (product.freeQuantity > 0 && product.paidQuantity > 0) {
              // Mixed: some free, some paid
              priceDisplay = this.formatPrice(product.price);
            } else if (product.freeQuantity > 0) {
              // All free
              priceDisplay = `<span class="multibuy-mini-basket__product-price--free">${freeLabel}</span>`;
            } else {
              // All paid
              priceDisplay = this.formatPrice(product.price);
            }
            
            // Create product HTML
            const productHTML = `
              <div class="multibuy-mini-basket__product" style="margin-bottom: 12px;">
                <div class="multibuy-mini-basket__product-image">
                  <img src="${product.image}" alt="${product.title}">
                </div>
                <div class="multibuy-mini-basket__product-info">
                  <div class="multibuy-mini-basket__product-details">
                    <p class="multibuy-mini-basket__product-title">${product.title}</p>
                    <p class="multibuy-mini-basket__product-variant">${
                      (() => {
                        if (product.variantOptions && product.variantOptions.length > 0) {
                          return product.variantOptions.map(opt => `${opt.name}: ${opt.value}`).join('<br>');
                        } else if (product.variantColor) {
                          return 'Color: ' + product.variantColor;
                        }
                        return '';
                      })()
                    }</p>
                  </div>
                  <div style="text-align: right;">
                    <p class="multibuy-mini-basket__product-price">${priceDisplay}</p>
                    <p class="multibuy-mini-basket__product-variant">${this.locale?.quantityLabel || 'Qty'}: ${product.quantity}</p>
                  </div>
                  <button type="button" class="multibuy-mini-basket__remove-product">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <g opacity="0.5">
                        <path d="M12 4L4 12" stroke="black" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M4 4L12 12" stroke="black" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                      </g>
                    </svg>
                  </button>
                </div>
              </div>
            `;
            container.insertAdjacentHTML('beforeend', productHTML);
          });
        });
      } else {
        secondaryEmptyStates.forEach(el => el.style.display = 'flex');
        secondaryContainers.forEach(el => {
          el.style.display = 'none';
          el.innerHTML = '';
        });
      }
      
      // Update price breakdown and button
      this.updatePriceBreakdown(total);
      this.addButtons.forEach(btn => btn.disabled = false);
    }

    // Update progress bar
    this.updateProgressBar();
    
    // Update shipping info text
    const shippingTexts = this.querySelectorAll('.multibuy-mini-basket__shipping-text');
    shippingTexts.forEach(el => el.textContent = this.locale?.shippingInfo || '');
  }

  updatePriceBreakdown(subtotal) {
    const CHARMS_FOR_FREE = 3;
    const CHARM_PRICE = 1695; // €16.95 in cents
    
    // Calculate free charms
    const totalCharms = Array.from(this.selectedSecondaryProducts.values())
      .reduce((sum, product) => sum + product.quantity, 0);
    const freeCharms = Math.floor(totalCharms / CHARMS_FOR_FREE);
    const discount = freeCharms * CHARM_PRICE;
    const total = subtotal - discount;
    
    // Count total items (only count main if we have one)
    const hasMain = this.selectedMainProduct !== null;
    const itemCount = (hasMain ? 1 : 0) + totalCharms;
    
    // Update all price breakdowns (desktop + mobile)
    const breakdowns = this.querySelectorAll('.multibuy-mini-basket__price-breakdown');
    
    breakdowns.forEach(breakdown => {
      const rows = breakdown.querySelectorAll('.multibuy-mini-basket__price-row');
      const discountRow = breakdown.querySelector('.multibuy-mini-basket__price-row--discount');
      
      // Subtotal row
      if (rows[0]) {
        const label = rows[0].querySelector('.multibuy-mini-basket__price-label');
        const value = rows[0].querySelector('.multibuy-mini-basket__price-value');
        if (label) label.textContent = this.locale?.subtotal || 'Subtotal';
        if (value) value.textContent = this.formatPrice(subtotal);
      }
      
      // Discount row (only show if there's a discount)
      if (discountRow && freeCharms > 0) {
        discountRow.style.display = 'flex';
        const label = discountRow.querySelector('.multibuy-mini-basket__price-label');
        const value = discountRow.querySelector('.multibuy-mini-basket__price-value');
        
        // Use correct pluralization
        const freeCharmLabel = freeCharms === 1 
          ? this.locale?.freeCharmOne?.replace('__COUNT__', freeCharms)
          : this.locale?.freeCharmOther?.replace('__COUNT__', freeCharms);
        
        if (label) label.textContent = freeCharmLabel || `${freeCharms}×Gratis bedel${freeCharms > 1 ? 's' : ''}`;
        if (value) value.textContent = this.locale?.discount?.replace('__AMOUNT__', this.formatPrice(discount)) || `− ${this.formatPrice(discount)}`;
      } else if (discountRow) {
        discountRow.style.display = 'none';
      }
      
      // Total row
      const totalRow = breakdown.querySelector('.multibuy-mini-basket__price-row--total');
      if (totalRow) {
        const label = totalRow.querySelector('.multibuy-mini-basket__price-label');
        const value = totalRow.querySelector('.multibuy-mini-basket__price-value');
        if (label) label.textContent = this.locale?.total || 'Totaal';
        if (value) value.textContent = this.formatPrice(total);
      }
    });
    
    // Update button text with item count and total (only if we have items)
    if (itemCount > 0) {
      this.addButtons.forEach(btn => {
        const buttonText = this.locale?.addButtonItems
          ?.replace('__COUNT__', itemCount)
          ?.replace('__TOTAL__', this.formatPrice(total))
          || `Voeg ${itemCount} items toe · ${this.formatPrice(total)}`;
        btn.textContent = buttonText;
      });
    } else {
      // Empty state - show default button text
      this.addButtons.forEach(btn => {
        btn.textContent = this.locale?.addButton || 'Add to cart';
      });
    }
  }

  updateProgressBar() {
    const CHARMS_FOR_FREE = 3;
    const totalCharms = Array.from(this.selectedSecondaryProducts.values())
      .reduce((sum, product) => sum + product.quantity, 0);
    
    // Calculate progress (0-3 for current cycle)
    const currentCycleCharms = totalCharms % CHARMS_FOR_FREE;
    const completedCycles = Math.floor(totalCharms / CHARMS_FOR_FREE);
    
    // Charms needed is always for the NEXT free item
    // If at 0, 3, 6, 9... we need 3 more for the next one
    const charmsNeeded = currentCycleCharms === 0 ? 3 : (CHARMS_FOR_FREE - currentCycleCharms);
    
    // Show badge if we've completed at least one cycle
    const showBadge = completedCycles > 0;
    
    // Calculate percentage (each charm = 33.33%)
    const percentage = (currentCycleCharms / CHARMS_FOR_FREE) * 100;
    
    // Update all progress bars (desktop + mobile)
    const progressBars = this.querySelectorAll('.multibuy-progress-bar');
    
    progressBars.forEach(bar => {
      const fill = bar.querySelector('.multibuy-progress-bar__fill');
      const badge = bar.querySelector('.multibuy-progress-bar__badge');
      const badgeText = bar.querySelector('.multibuy-progress-bar__badge-text');
      const message = bar.querySelector('.multibuy-progress-bar__message');
      const segments = bar.querySelectorAll('.multibuy-progress-bar__segment');
      
      if (fill) {
        fill.style.width = `${percentage}%`;
      }
      
      // Update segment active states
      segments.forEach((segment, index) => {
        if (index < currentCycleCharms) {
          segment.classList.add('active');
        } else {
          segment.classList.remove('active');
        }
      });
      
      if (showBadge) {
        // Show badge with count of completed cycles
        if (badge) badge.style.display = 'flex';
        const badgeLabel = this.progressLocale?.badge?.replace('__COUNT__', completedCycles) || `${completedCycles}× GRATIS`;
        if (badgeText) badgeText.textContent = badgeLabel;
      } else {
        // Hide badge
        if (badge) badge.style.display = 'none';
      }
      
      // Always show message with correct count and pluralization
      if (message) {
        message.style.display = 'block';
        
        // Check if stoppers section exists and is visible (collection 2)
        const stoppersSection = this.querySelector('.multibuy-section--secondary-2');
        const hasStoppers = stoppersSection && stoppersSection.style.display !== 'none';
        
        // Use correct pluralization and version (with/without stoppers)
        let messageTemplate;
        if (hasStoppers) {
          messageTemplate = charmsNeeded === 1 
            ? this.progressLocale?.messageOneWithStoppers 
            : this.progressLocale?.messageOtherWithStoppers;
        } else {
          messageTemplate = charmsNeeded === 1 
            ? this.progressLocale?.messageOne 
            : this.progressLocale?.messageOther;
        }
        
        const messageText = messageTemplate?.replace('__COUNT__', charmsNeeded) 
          || `Voeg nog <strong>${charmsNeeded} bedel${charmsNeeded > 1 ? 's' : ''} toe</strong> voor 1 gratis`;
        message.innerHTML = messageText;
      }
    });
  }

  initProgressHeader() {
    const progressHeader = document.querySelector('[data-multibuy-progress-header]');
    if (!progressHeader) return;

    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateProgressHeader = () => {
      const currentScrollY = window.scrollY;
      const scrollingUp = currentScrollY < lastScrollY;
      const scrolledPastThreshold = currentScrollY > 100;

      if (scrollingUp && scrolledPastThreshold) {
        progressHeader.classList.add('is-visible');
      } else {
        progressHeader.classList.remove('is-visible');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateProgressHeader);
        ticking = true;
      }
    });

    // Scroll step into view within progress header
    const scrollStepIntoView = (step) => {
      const container = progressHeader.querySelector('.multibuy-progress-header__inner');
      if (!container) return;

      const stepRect = step.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Check if step is fully visible
      const isFullyVisible = stepRect.left >= containerRect.left && stepRect.right <= containerRect.right;

      if (!isFullyVisible) {
        // Scroll step into view with some padding
        step.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    };

    // Update active step based on scroll position
    const updateActiveStep = () => {
      const mainSection = this.querySelector('.multibuy-section--main');
      const secondarySection = this.querySelector('.multibuy-section--secondary-1');
      const stoppersSection = this.querySelector('.multibuy-section--secondary-2');
      
      const steps = progressHeader.querySelectorAll('.multibuy-progress-header__step');
      const scrollY = window.scrollY + (window.innerHeight / 3);

      steps.forEach(step => step.classList.remove('is-active'));

      let activeStep = null;

      if (stoppersSection && stoppersSection.style.display !== 'none' && scrollY >= stoppersSection.offsetTop) {
        steps[2]?.classList.add('is-active');
        activeStep = steps[2];
      } else if (secondarySection && scrollY >= secondarySection.offsetTop) {
        steps[1]?.classList.add('is-active');
        activeStep = steps[1];
      } else if (mainSection) {
        steps[0]?.classList.add('is-active');
        activeStep = steps[0];
      }

      // Scroll active step into view
      if (activeStep) {
        scrollStepIntoView(activeStep);
      }
    };

    let scrollTicking = false;
    window.addEventListener('scroll', () => {
      if (!scrollTicking) {
        window.requestAnimationFrame(() => {
          updateActiveStep();
          scrollTicking = false;
        });
        scrollTicking = true;
      }
    });

    // Initial update
    updateActiveStep();

    // Click handler for steps - scroll to section
    const steps = progressHeader.querySelectorAll('.multibuy-progress-header__step');
    steps.forEach((step, index) => {
      step.addEventListener('click', () => {
        // Scroll step into view within progress header
        scrollStepIntoView(step);
        
        let targetSection;
        
        if (index === 0) {
          targetSection = this.querySelector('.multibuy-section--main');
        } else if (index === 1) {
          targetSection = this.querySelector('.multibuy-section--secondary-1');
        } else if (index === 2) {
          targetSection = this.querySelector('.multibuy-section--secondary-2');
        }
        
        if (targetSection) {
          const headerOffset = document.querySelector('.shopify-section-header')?.offsetHeight || 0;
          const announcementBarOffset = document.querySelector('.announcement-bar')?.offsetHeight || 0;
          const progressHeaderOffset = progressHeader.offsetHeight || 0;
          const totalOffset = headerOffset + announcementBarOffset + progressHeaderOffset + 20; // 20px extra padding
          
          const elementPosition = targetSection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - totalOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      });
    });

    // Update step 3 visibility based on active collection
    const updateStepVisibility = () => {
      const step3 = progressHeader.querySelector('[data-step="3"]');
      if (!step3) return;

      const stoppersSection = this.querySelector('.multibuy-section--secondary-2');
      if (stoppersSection && stoppersSection.style.display !== 'none') {
        step3.style.display = '';
      } else {
        step3.style.display = 'none';
      }
    };

    // Watch for tab changes
    const tabButtons = this.querySelectorAll('.multibuy-tab');
    tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        setTimeout(updateStepVisibility, 100);
      });
    });

    // Initial visibility update
    updateStepVisibility();
  }

  initMobileBasketFooterDetection() {
    const mobileBasket = document.querySelector('.multibuy-mini-basket-mobile');
    if (!mobileBasket) return;

    const footer = document.querySelector('.shopify-section-footer, footer, [data-section-type="footer"]');
    if (!footer) return;

    let ticking = false;
    let hasScrolled = false;

    const checkScrollAndFooter = () => {
      const scrollY = window.scrollY;
      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Show basket after user scrolls down 50px
      if (scrollY > 50 && !hasScrolled) {
        hasScrolled = true;
        mobileBasket.classList.add('is-scrolled');
      }

      // Hide basket when footer is visible (only if already scrolled)
      if (hasScrolled) {
        if (footerRect.top < windowHeight) {
          mobileBasket.classList.add('is-hidden-by-footer');
        } else {
          mobileBasket.classList.remove('is-hidden-by-footer');
        }
      }

      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(checkScrollAndFooter);
        ticking = true;
      }
    });

    // Initial check
    checkScrollAndFooter();
  }
}

// Register the custom element
if (!customElements.get('multibuy-manager')) {
  customElements.define('multibuy-manager', MultibuyManager);
}
